import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  Plane, ArrowRight, Shield, Star, BadgeCheck, Package,
  Luggage, Clock, Search, Send, MessageSquare, List,
  Headphones, DollarSign, Zap, Repeat, Wallet, CreditCard, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── HERO ────────────────────────────────────────────────────────────
const HeroSection = ({ navigate, user }) => (
  <section className="relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-background" />
    <div className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full bg-[#D4AF37]/[0.04] blur-[150px]" />
    <div className="absolute bottom-[-200px] left-[-100px] w-[500px] h-[500px] rounded-full bg-[#D4AF37]/[0.03] blur-[120px]" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-36 sm:pb-24">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium mb-8 tracking-wide uppercase">
          <Shield className="w-3.5 h-3.5" />
          Plateforme sécurisée
        </div>

        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1] mb-5"
          data-testid="hero-title"
        >
          La plateforme d'échange entre le{' '}
          <span className="text-[#D4AF37]">Canada</span> et le{' '}
          <span className="text-[#D4AF37]">Cameroun</span>
        </h1>

        <p className="text-base lg:text-lg text-muted-foreground max-w-xl mx-auto mb-10">
          Transport de colis et échange de devises en toute sécurité
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
          <Button
            className="h-13 px-8 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#D4AF37]/20 text-base"
            onClick={() => navigate('/listings')}
            data-testid="hero-send-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Envoyer un colis
          </Button>
          <Button
            className="h-13 px-8 bg-muted/50 hover:bg-muted border border-border hover:border-[#D4AF37]/30 text-foreground font-medium rounded-xl transition-all text-base"
            onClick={() => navigate('/currency/listings')}
            data-testid="hero-exchange-btn"
          >
            <Repeat className="w-4 h-4 mr-2" />
            Échanger de l'argent (0 commission)
          </Button>
        </div>

        <div className="flex flex-wrap gap-6 justify-center" data-testid="hero-badges">
          {[
            { icon: Shield, label: 'Sécurisé' },
            { icon: Zap, label: 'Simple' },
            { icon: Clock, label: 'Rapide' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-muted-foreground">
              <div className="w-7 h-7 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
              </div>
              <span className="text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── SERVICES SECTION ────────────────────────────────────────────────
const ServicesSection = ({ navigate }) => (
  <section className="py-20 sm:py-24" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3">
          Deux services, une seule <span className="text-[#D4AF37]">plateforme</span>
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Choisissez le service qui correspond à vos besoins
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
        {/* Transport Card */}
        <div
          className="group relative rounded-2xl bg-muted/30 border border-border hover:border-[#D4AF37]/30 overflow-hidden transition-all duration-300"
          data-testid="service-card-transport"
        >
          {/* Top accent */}
          <div className="h-1 bg-gradient-to-r from-[#D4AF37] to-[#D4AF37]/40" />

          <div className="p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/15 transition-colors">
                <Package className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground">Transport de colis</h3>
                <p className="text-xs text-muted-foreground">Canada &harr; Cameroun</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Envoyez vos colis entre le Canada et le Cameroun via des voyageurs vérifiés. KACOS gère la logistique de bout en bout.
            </p>

            <div className="space-y-3 mb-7">
              {[
                'Voyageurs vérifiés avec billet validé',
                'Paiement sécurisé en séquestre',
                'Suivi de livraison en temps réel',
                'Ramassage et livraison coordonnés',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>

            <Button
              className="w-full h-11 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all hover:scale-[1.01] active:scale-[0.99]"
              onClick={() => navigate('/listings')}
              data-testid="service-transport-btn"
            >
              Trouver un transport
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Devises Card */}
        <div
          className="group relative rounded-2xl bg-muted/30 border border-border hover:border-[#D4AF37]/30 overflow-hidden transition-all duration-300"
          data-testid="service-card-devises"
        >
          <div className="h-1 bg-gradient-to-r from-[#D4AF37]/40 to-[#D4AF37]" />

          <div className="p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/15 transition-colors">
                <Repeat className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground">Échange de devises</h3>
                <p className="text-xs text-muted-foreground">CAD &harr; XAF</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Échangez vos dollars canadiens (CAD) en francs CFA (XAF) à des taux compétitifs. Transactions rapides et sécurisées.
            </p>

            <div className="space-y-3 mb-7">
              {[
                'Taux compétitifs mis à jour en continu',
                'Transactions sécurisées par KACOS',
                'Multiples moyens de paiement acceptés',
                'Processus simple et rapide',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>

            <Button
              className="w-full h-11 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all hover:scale-[1.01] active:scale-[0.99]"
              onClick={() => navigate('/currency/listings')}
              data-testid="service-devises-btn"
            >
              Échanger maintenant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── HOW IT WORKS (DUAL FLOW) ────────────────────────────────────────
const HowItWorks = () => (
  <section className="py-20 sm:py-24 border-t border-border" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/40 border border-border text-muted-foreground text-xs font-medium mb-5 tracking-wide uppercase">
          Comment ça marche
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
          Simple en <span className="text-[#D4AF37]">3 étapes</span>
        </h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Transport Flow */}
        <div data-testid="how-transport-flow">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground">Transport de colis</h3>
          </div>
          <div className="space-y-6">
            {[
              { num: '01', icon: Plane, title: 'Publier un voyage', desc: 'Les voyageurs publient leurs vols avec les kilos disponibles et les tarifs.' },
              { num: '02', icon: Search, title: 'Réserver des kilos', desc: 'Trouvez un voyageur sur votre trajet, choisissez vos kilos et réservez.' },
              { num: '03', icon: Shield, title: 'KACOS gère la livraison', desc: 'Paiement sécurisé, ramassage coordonné et suivi jusqu\'à la livraison.' },
            ].map(({ num, icon: Icon, title, desc }, i) => (
              <div key={num} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  {i < 2 && <div className="w-px h-full bg-[#D4AF37]/10 my-1" />}
                </div>
                <div className="pb-2">
                  <p className="text-[10px] text-[#D4AF37]/50 uppercase tracking-widest mb-1">Étape {num}</p>
                  <h4 className="font-heading font-semibold text-foreground mb-1">{title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Devises Flow */}
        <div data-testid="how-devises-flow">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Repeat className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground">Échange de devises</h3>
          </div>
          <div className="space-y-6">
            {[
              { num: '01', icon: Wallet, title: 'Entrer le montant', desc: 'Sélectionnez une offre et indiquez le montant que vous souhaitez échanger.' },
              { num: '02', icon: CreditCard, title: 'Effectuer le paiement', desc: 'Payez de manière sécurisée via le moyen de paiement de votre choix.' },
              { num: '03', icon: DollarSign, title: 'Recevoir votre argent', desc: 'Recevez vos fonds rapidement une fois la transaction confirmée par KACOS.' },
            ].map(({ num, icon: Icon, title, desc }, i) => (
              <div key={num} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  {i < 2 && <div className="w-px h-full bg-[#D4AF37]/10 my-1" />}
                </div>
                <div className="pb-2">
                  <p className="text-[10px] text-[#D4AF37]/50 uppercase tracking-widest mb-1">Étape {num}</p>
                  <h4 className="font-heading font-semibold text-foreground mb-1">{title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── TRUST SECTION ───────────────────────────────────────────────────
const TrustSection = () => (
  <section className="py-20 sm:py-24 border-t border-border" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3">
          Pourquoi choisir <span className="text-[#D4AF37]">KACOS</span>
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Une plateforme fiable pour vos échanges entre le Canada et le Cameroun
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: Shield, title: 'Transactions sécurisées', desc: 'Vos fonds sont protégés en séquestre jusqu\'à la confirmation de chaque transaction.' },
          { icon: BadgeCheck, title: 'Utilisateurs vérifiés', desc: 'Chaque utilisateur est vérifié par email, téléphone et validation de ses documents.' },
          { icon: Headphones, title: 'Support client', desc: 'Une équipe dédiée vous accompagne à chaque étape, du début à la fin.' },
          { icon: CheckCircle, title: 'Processus fiable', desc: 'Billets validés, avis transparents et suivi en temps réel de vos opérations.' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="group p-6 rounded-2xl bg-white/[0.02] border border-border hover:border-[#D4AF37]/20 transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/15 transition-colors">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading font-semibold text-foreground mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── REVIEWS ─────────────────────────────────────────────────────────
const ReviewsSection = ({ topReviews, user, navigate, token, loadTopReviews }) => {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [allReviewsOpen, setAllReviewsOpen] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [allReviewsStats, setAllReviewsStats] = useState({ total: 0, average_rating: 0 });

  const loadAllReviews = async () => {
    try {
      const res = await axios.get(`${API}/platform-reviews/all`);
      setAllReviews(res.data.reviews || []);
      setAllReviewsStats({ total: res.data.total || 0, average_rating: res.data.average_rating || 0 });
      setAllReviewsOpen(true);
    } catch { toast.error('Erreur lors du chargement des avis'); }
  };

  const handleSubmitReview = async () => {
    if (!reviewComment.trim() || reviewComment.trim().length < 5) {
      toast.error('Le commentaire doit contenir au moins 5 caractères');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/platform-reviews/submit`, { rating: reviewRating, comment: reviewComment.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Merci pour votre avis !');
      setReviewOpen(false);
      setReviewComment('');
      setReviewRating(5);
      loadTopReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setSubmitting(false); }
  };

  return (
    <section className="py-20 sm:py-24 border-t border-border" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Ce que disent nos <span className="text-[#D4AF37]">utilisateurs</span>
          </h2>
          <p className="text-sm text-muted-foreground">Des avis authentiques de notre communauté</p>
        </div>

        {topReviews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {topReviews.map((review, i) => (
              <div key={review.id || i} className="p-5 rounded-2xl bg-white/[0.02] border border-border hover:border-[#D4AF37]/15 transition-all" data-testid={`review-card-${i}`}>
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-foreground/15'}`} />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed italic mb-4">"{review.comment}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-border">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#D4AF37]">{review.user_name?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-foreground">{review.user_name}</p>
                    <p className="text-[11px] text-foreground/30">{new Date(review.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 mb-10">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-foreground/10" />
            <p className="text-foreground/30 text-sm">Aucun avis pour le moment. Soyez le premier !</p>
          </div>
        )}

        <div className="text-center flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Button
            variant="outline"
            className="bg-muted/40 border-border text-muted-foreground hover:text-[#D4AF37] hover:border-[#D4AF37]/30 hover:bg-muted/50 rounded-xl"
            onClick={loadAllReviews}
            data-testid="view-all-reviews-btn"
          >
            <List className="w-4 h-4 mr-2" /> Voir tous les avis
          </Button>
          {user ? (
            <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl" data-testid="leave-review-btn">
                  <Star className="w-4 h-4 mr-2" /> Laisser un avis
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Donner votre avis sur Kacos</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Votre note</label>
                    <div className="flex gap-1" data-testid="review-stars">
                      {[1, 2, 3, 4, 5].map(s => (
                        <button key={s} type="button" onClick={() => setReviewRating(s)}
                          className="p-1 hover:scale-110 transition-transform" data-testid={`star-${s}`}>
                          <Star className={`w-8 h-8 ${s <= reviewRating ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground hover:text-yellow-400'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Votre commentaire</label>
                    <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Partagez votre expérience avec Kacos..." rows={4} data-testid="review-comment" />
                    <p className="text-xs text-muted-foreground mt-1">Minimum 5 caractères</p>
                  </div>
                  <Button className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl" onClick={handleSubmitReview}
                    disabled={submitting || reviewComment.trim().length < 5} data-testid="submit-review-btn">
                    {submitting ? 'Envoi...' : 'Envoyer mon avis'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Button className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl" onClick={() => navigate('/login')} data-testid="login-to-review-btn">
              <Star className="w-4 h-4 mr-2" /> Connectez-vous pour laisser un avis
            </Button>
          )}
        </div>

        {/* All Reviews Dialog */}
        <Dialog open={allReviewsOpen} onOpenChange={setAllReviewsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="all-reviews-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#D4AF37]" />
                Tous les avis ({allReviewsStats.total})
              </DialogTitle>
              {allReviewsStats.average_rating > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${s <= Math.round(allReviewsStats.average_rating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-muted-foreground'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{allReviewsStats.average_rating}/5</span>
                  <span className="text-sm text-muted-foreground">({allReviewsStats.total} avis)</span>
                </div>
              )}
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {allReviews.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>Aucun avis pour le moment</p>
                </div>
              ) : (
                allReviews.map((review, i) => (
                  <div key={review.id || i} className="border rounded-lg p-4 hover:border-[#D4AF37]/30 transition-colors" data-testid={`all-review-${i}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-muted-foreground'}`} />
                        ))}
                      </div>
                      <p className="text-sm leading-relaxed">"{review.comment}"</p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t">
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#D4AF37]">{review.user_name?.charAt(0)?.toUpperCase() || '?'}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{review.user_name}</p>
                        <p className="text-xs text-muted-foreground">{new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

// ─── FINAL CTA ───────────────────────────────────────────────────────
const FinalCTA = ({ user, navigate }) => (
  <section className="py-20 sm:py-24" data-testid="cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#D4AF37]/10 via-background to-[#D4AF37]/5 border border-[#D4AF37]/15 p-10 sm:p-14 text-center">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-[80px]" />

        <div className="relative z-10">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4" data-testid="cta-title">
            Commencez maintenant avec <span className="text-[#D4AF37]">KACOS</span>
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-8 text-sm sm:text-base">
            Rejoignez des milliers d'utilisateurs qui font confiance à KACOS pour leurs échanges entre le Canada et le Cameroun.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              className="h-12 px-8 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#D4AF37]/20"
              onClick={() => navigate('/listings')}
              data-testid="cta-send-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Envoyer un colis
            </Button>
            <Button
              className="h-12 px-8 bg-muted/50 hover:bg-muted border border-border hover:border-[#D4AF37]/30 text-foreground font-medium rounded-xl transition-all"
              onClick={() => navigate('/currency/listings')}
              data-testid="cta-exchange-btn"
            >
              <Repeat className="w-4 h-4 mr-2" />
              Échanger de l'argent (0 commission)
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── MAIN PAGE ───────────────────────────────────────────────────────
export default function PlatformHubPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [topReviews, setTopReviews] = useState([]);

  useEffect(() => {
    loadTopReviews();
  }, []);

  const loadTopReviews = async () => {
    try {
      const res = await axios.get(`${API}/platform-reviews/top`);
      setTopReviews(res.data.reviews || []);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="hub-page">
      <Navbar />

      <HeroSection navigate={navigate} user={user} />
      <ServicesSection navigate={navigate} />
      <HowItWorks />
      <TrustSection />
      <ReviewsSection topReviews={topReviews} user={user} navigate={navigate} token={token} loadTopReviews={loadTopReviews} />
      <FinalCTA user={user} navigate={navigate} />

      <Footer />
    </div>
  );
}
